// ui-kit/fotounit — pengambil foto kondisi unit, dipakai di Terima Servis dan saat serah-terima.
//
// Sengketa "casing saya tidak penyok waktu masuk" adalah keluhan termahal di bengkel HP, dan
// selama ini satu-satunya bukti toko cuma kalimat yang diketik CS. Foto diunggah SAAT DIPILIH,
// bukan saat Simpan: staf langsung tahu kalau unggahannya gagal, dan menyimpan tiket tidak
// menunggu jaringan.
import { compressImage, uploadFile, signedUrl, FOTO } from '@core/storage';
import { esc } from '@domain/format';

export interface FotoUnit { path: string; fase: 'terima' | 'serah'; at: string; by: string }

const MAKS_DEFAULT = 4;

/**
 * Pasang pengambil foto di `host`. Mengembalikan pembaca nilainya — pemanggil tinggal menyimpan
 * hasilnya bersama tiket. Foto yang gagal diunggah TIDAK masuk daftar, jadi tak pernah ada jalur
 * yang menunjuk berkas yang tidak ada.
 */
export function fotoUnitPicker(
  host: HTMLElement,
  opt: { outlet: string | null; fase: 'terima' | 'serah'; oleh: string; awal?: FotoUnit[]; maks?: number },
): { nilai: () => FotoUnit[] } {
  const maks = opt.maks ?? MAKS_DEFAULT;
  const daftar: FotoUnit[] = [...(opt.awal ?? [])];
  const folder = opt.outlet || 'umum';
  const id = 'fu_' + Math.random().toString(36).slice(2, 8);

  host.innerHTML = `
    <div class="field">
      <label>📷 Foto kondisi unit <span style="color:var(--muted);font-weight:400">(maks ${maks} — bukti bila kondisi fisik dipersoalkan)</span></label>
      <input type="file" id="${id}_in" accept="image/*" capture="environment" multiple>
      <div id="${id}_list" style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px"></div>
      <div class="hint" id="${id}_hint"></div>
    </div>`;

  const list = host.querySelector('#' + id + '_list') as HTMLElement;
  const hint = host.querySelector('#' + id + '_hint') as HTMLElement;
  const input = host.querySelector('#' + id + '_in') as HTMLInputElement;

  const gambar = async (): Promise<void> => {
    list.innerHTML = daftar.map((_f, i) =>
      `<div style="position:relative"><img data-th="${i}" alt="Foto unit ${i + 1}" style="width:72px;height:72px;object-fit:cover;border:1px solid var(--line);border-radius:6px;background:var(--ice)">
        <button type="button" class="ico-btn" data-del="${i}" title="Hapus foto ${i + 1}" aria-label="Hapus foto ${i + 1}"
          style="position:absolute;top:-6px;right:-6px;background:var(--surface);border-radius:50%">🗑️</button></div>`).join('');
    hint.textContent = daftar.length ? `${daftar.length} foto siap disimpan.` : 'Belum ada foto.';
    for (const [i, f] of daftar.entries()) {
      const img = list.querySelector(`img[data-th="${i}"]`) as HTMLImageElement | null;
      if (!img) continue;
      // Bucket privat → thumbnail lewat signed URL. Gagal (mis. berkas terhapus) cukup
      // menyisakan kotak kosong; jangan menggagalkan seluruh form karena satu gambar.
      try { img.src = await signedUrl('unit', f.path, 300); } catch { img.alt = 'gagal dimuat'; }
    }
    list.querySelectorAll<HTMLElement>('button[data-del]').forEach((b) => b.addEventListener('click', () => {
      // Sengaja TIDAK menghapus berkasnya: staf sering salah pilih lalu ingin kembali, dan
      // berkas tanpa penunjuk sudah punya rumah sendiri di menu Kelola Penyimpanan.
      daftar.splice(+(b.dataset.del ?? -1), 1); void gambar();
    }));
  };

  input.addEventListener('change', () => void (async () => {
    const berkas = [...(input.files ?? [])];
    input.value = '';
    for (const file of berkas) {
      if (daftar.length >= maks) { hint.textContent = `Maksimal ${maks} foto.`; break; }
      hint.textContent = 'Mengunggah…';
      try {
        const blob = await compressImage(file, FOTO.bukti.dim, FOTO.bukti.q);
        const path = await uploadFile('unit', folder, blob, 'jpg', 'image/jpeg');
        daftar.push({ path, fase: opt.fase, at: new Date().toISOString(), by: opt.oleh });
      } catch (e) {
        hint.innerHTML = `<span style="color:var(--danger)">Gagal mengunggah ${esc(file.name)}: ${esc((e as Error).message)}</span>`;
        return;
      }
    }
    await gambar();
  })());

  void gambar();
  return { nilai: () => [...daftar] };
}

/** Petak thumbnail read-only untuk panel detail tiket. */
export async function fotoUnitGrid(host: HTMLElement, foto: FotoUnit[]): Promise<void> {
  if (!foto.length) { host.innerHTML = ''; return; }
  const label = (f: FotoUnit): string => (f.fase === 'serah' ? 'saat diserahkan' : 'saat diterima');
  host.innerHTML = `<div class="info-row" style="display:block"><span class="k">📷 Foto kondisi unit</span>
    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:4px">${foto.map((f, i) =>
      `<a data-fu="${i}" href="#" title="${esc(label(f))} · ${esc((f.at || '').slice(0, 10))}${f.by ? ' · ' + esc(f.by) : ''}">
        <img data-fi="${i}" alt="Foto unit ${esc(label(f))}" style="width:84px;height:84px;object-fit:cover;border:1px solid var(--line);border-radius:6px;background:var(--ice)"></a>`).join('')}</div></div>`;
  for (const [i, f] of foto.entries()) {
    const img = host.querySelector(`img[data-fi="${i}"]`) as HTMLImageElement | null;
    const a = host.querySelector(`a[data-fu="${i}"]`) as HTMLAnchorElement | null;
    try {
      const url = await signedUrl('unit', f.path, 600);
      if (img) img.src = url;
      if (a) { a.href = url; a.target = '_blank'; a.rel = 'noopener'; }
    } catch { if (img) img.alt = 'gagal dimuat'; }
  }
}
